/**
 * The route a person is sent back to after signing in again.
 *
 * `RequireSession` and `RouteAccessGuard` already carry it in router state (`state.from`), and that
 * stays the first choice: it is in memory, so nothing about it reaches a URL, a log or a referrer.
 * It survives only a `<Navigate>` inside React, though, and a session that goes stale does not end
 * inside React — `Session.logout` assigns `location`, which is a new document and an empty router
 * state. So the route travels in the URL for that one case.
 */

export const LOGIN_ROUTE = "/login";
export const LOGIN_RETURN_ROUTE_PARAMETER = "returnTo";

/**
 * The login route, carrying where to come back to.
 */
export function buildLoginRoute(returnTo?: string): string {
  const safeReturnRoute = readSafeReturnRoute(returnTo);
  if (!safeReturnRoute) return LOGIN_ROUTE;

  return `${LOGIN_ROUTE}?${LOGIN_RETURN_ROUTE_PARAMETER}=${encodeURIComponent(safeReturnRoute)}`;
}

/**
 * The route named by a login URL's query string, or `undefined` when it names none this app will
 * navigate to.
 */
export function readLoginReturnRoute(search: string): string | undefined {
  return readSafeReturnRoute(new URLSearchParams(search).get(LOGIN_RETURN_ROUTE_PARAMETER));
}

/**
 * Goes to the login screen, carrying where to come back to.
 *
 * A whole document load, not a router navigation: a session ends outside React — in a base query,
 * in an Ably auth call, in a timer — and nothing there holds a `navigate`. The load also throws away
 * every cache and every store the ended session filled, which is the point.
 */
export function goToLogin(returnTo?: string): void {
  globalThis.location.assign(buildLoginRoute(returnTo));
}

/**
 * Where the browser is now, ready to be handed to {@link buildLoginRoute}.
 *
 * Answers `undefined` on the login screen itself, so signing out from there does not ask the login
 * form to send the person back to the login form.
 */
export function currentReturnRoute(): string | undefined {
  const location = globalThis.location;
  if (!location) return undefined;

  return readSafeReturnRoute(`${location.pathname}${location.search}${location.hash}`);
}

/**
 * Accepts only a path on this site.
 *
 * A return route reaches the login form from a URL anybody can write, and the form navigates to it
 * with no further check. `//evil.example/x` and `https://evil.example/x` are both valid values of
 * `window.location.href`, so a route that merely "starts with a slash" is an open redirect: a
 * phishing mail can link to this app's own login page and land the person somewhere else the moment
 * they sign in. One leading slash, and no second one, is the whole rule — and `\` counts as a
 * separator in a URL, so it is refused in that position too.
 */
function readSafeReturnRoute(candidate: string | null | undefined): string | undefined {
  if (!candidate) return undefined;
  if (!candidate.startsWith("/")) return undefined;
  if (candidate.startsWith("//") || candidate.startsWith("/\\")) return undefined;
  if (candidate === LOGIN_ROUTE || candidate.startsWith(`${LOGIN_ROUTE}?`) || candidate.startsWith(`${LOGIN_ROUTE}/`)) {
    return undefined;
  }

  return candidate;
}
